/**
 * FBT Variant Hider
 *
 * Hides variants in the Frequently Bought Together (Code Black Belt) widget
 * based on the variant.metafields.custom.variant_hide metafield.
 *
 * No Shopify app or Admin API required - works entirely at the theme level.
 */

(function () {
  'use strict';

  // Inject CSS to hide disabled options in FBT widget
  (function injectStyles() {
    var style = document.createElement('style');
    style.textContent = `
      .cbb-frequently-bought-container .cbb-recommendations-variant-select option[disabled][data-fbt-hidden="true"] {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  })();

  // Configuration
  var CONFIG = {
    FBT_CONTAINER_SELECTOR: '.cbb-frequently-bought-container',
    PRODUCT_LINK_SELECTOR: 'a[href*="/products/"]',
    VARIANT_SELECT_SELECTOR: '.cbb-recommendations-variant-select',
    VARIANT_OPTION_SELECTOR: 'option[data-variant-id]',
    JSON_ENDPOINT_TEMPLATE: '/products/{handle}?view=variants-json',
    MAX_CONCURRENT_REQUESTS: 3,
    REQUEST_TIMEOUT: 10000
  };

  // Cache for fetched product data
  var productCache = {};
  var pendingRequests = {};
  var initialProcessingComplete = false;
  var isProcessing = false;
  var observer = null;
  var widgetObserver = null;
  var hiddenVariantIds = [];

  /**
   * Extract product handle from a URL
   */
  function extractProductHandle(url) {
    if (!url) return null;
    var match = url.match(/\/products\/([^\/\?]+)/);
    return match ? match[1] : null;
  }

  /**
   * Extract all unique product handles from the FBT widget
   */
  function getProductHandles(container) {
    var handles = [];
    var links = container.querySelectorAll(CONFIG.PRODUCT_LINK_SELECTOR);

    for (var i = 0; i < links.length; i++) {
      var handle = extractProductHandle(links[i].getAttribute('href'));
      if (handle && handles.indexOf(handle) === -1) {
        handles.push(handle);
      }
    }

    return handles;
  }

  /**
   * Fetch variant data for a product via the JSON template
   */
  function fetchVariantData(handle) {
    if (productCache[handle]) {
      return Promise.resolve(productCache[handle]);
    }

    if (pendingRequests[handle]) {
      return pendingRequests[handle];
    }

    var url = CONFIG.JSON_ENDPOINT_TEMPLATE.replace('{handle}', handle);
    var promise = new Promise(function (resolve, reject) {
      var timeout = setTimeout(function () {
        reject(new Error('Request timeout for handle: ' + handle));
      }, CONFIG.REQUEST_TIMEOUT);

      fetch(url)
        .then(function (response) {
          clearTimeout(timeout);
          if (!response.ok) {
            throw new Error('HTTP ' + response.status + ' for ' + url);
          }
          return response.json();
        })
        .then(function (data) {
          productCache[handle] = data;
          delete pendingRequests[handle];
          resolve(data);
        })
        .catch(function (error) {
          clearTimeout(timeout);
          delete pendingRequests[handle];
          console.error('FBT Variant Hider: Failed to fetch data for ' + handle, error);
          resolve(null);
        });
    });

    pendingRequests[handle] = promise;
    return promise;
  }

  /**
   * Fetch variant data for multiple products with batching
   */
  function fetchVariantDataBatch(handles) {
    var batches = [];
    var currentBatch = [];

    for (var i = 0; i < handles.length; i++) {
      currentBatch.push(handles[i]);
      if (currentBatch.length >= CONFIG.MAX_CONCURRENT_REQUESTS) {
        batches.push(currentBatch);
        currentBatch = [];
      }
    }
    if (currentBatch.length > 0) {
      batches.push(currentBatch);
    }

    return batches.reduce(function (promise, batch) {
      return promise.then(function (results) {
        return Promise.all(batch.map(fetchVariantData)).then(function (batchResults) {
          return results.concat(batchResults.filter(function (data) {
            return data !== null;
          }));
        });
      });
    }, Promise.resolve([]));
  }

  /**
   * Build list of hidden variant IDs from product data
   */
  function getHiddenVariantIds(productsData) {
    var hiddenIds = [];

    for (var i = 0; i < productsData.length; i++) {
      var product = productsData[i];
      if (product && product.variants) {
        for (var j = 0; j < product.variants.length; j++) {
          var variant = product.variants[j];
          if (variant.variant_hide === true) {
            hiddenIds.push(String(variant.id));
          }
        }
      }
    }

    return hiddenIds;
  }

  /**
   * Disable hidden variant options in the FBT widget dropdowns
   */
  function removeHiddenVariantOptions(container, variantIdsToHide, storeGlobally) {
    if (!variantIdsToHide || variantIdsToHide.length === 0) {
      return;
    }

    if (storeGlobally) {
      hiddenVariantIds = variantIdsToHide.slice();
    }

    var selects = container.querySelectorAll(CONFIG.VARIANT_SELECT_SELECTOR);

    for (var i = 0; i < selects.length; i++) {
      var select = selects[i];
      var options = Array.prototype.slice.call(select.querySelectorAll(CONFIG.VARIANT_OPTION_SELECTOR));

      for (var j = 0; j < options.length; j++) {
        var option = options[j];
        var variantId = option.getAttribute('data-variant-id');

        if (variantIdsToHide.indexOf(variantId) !== -1) {
          option.disabled = true;
          option.setAttribute('data-fbt-hidden', 'true');
        }
      }
    }
  }

  /**
   * Re-disable variants after FBT widget re-renders
   */
  function reHideVariants() {
    if (hiddenVariantIds.length === 0) {
      return;
    }

    var container = document.querySelector(CONFIG.FBT_CONTAINER_SELECTOR);
    if (container) {
      removeHiddenVariantOptions(container, hiddenVariantIds, false);
    }
  }

  /**
   * Set up persistent observer to watch for FBT widget re-renders
   */
  function setupPersistentObserver(container) {
    if (widgetObserver) {
      return;
    }

    widgetObserver = new MutationObserver(function () {
      reHideVariants();
    });

    widgetObserver.observe(container, { childList: true, subtree: true });
  }

  /**
   * Finish processing after all product data is collected
   */
  function finishProcessing(productsData, container) {
    var hiddenIds = getHiddenVariantIds(productsData);
    removeHiddenVariantOptions(container, hiddenIds, true);
    setupPersistentObserver(container);
    initialProcessingComplete = true;
    isProcessing = false;
  }

  /**
   * Process the FBT widget to hide variants
   */
  function processFBTWidget(container) {
    if (isProcessing) {
      return;
    }

    if (initialProcessingComplete && hiddenVariantIds.length > 0) {
      removeHiddenVariantOptions(container, hiddenVariantIds, false);
      return;
    }

    isProcessing = true;

    var productsData = [];
    if (window.FBTMainProductData && window.FBTMainProductData.variants) {
      productsData.push(window.FBTMainProductData);
    }

    var handles = getProductHandles(container);

    if (handles.length === 0) {
      isProcessing = false;
      if (productsData.length > 0) {
        finishProcessing(productsData, container);
      }
      return;
    }

    fetchVariantDataBatch(handles).then(function (fetchedData) {
      productsData = productsData.concat(fetchedData);
      finishProcessing(productsData, container);
    }).catch(function (error) {
      console.error('FBT Variant Hider: Error processing widget', error);
      isProcessing = false;
    });
  }

  /**
   * Find and process the FBT widget
   */
  function findAndProcessFBTWidget() {
    if (isProcessing) {
      return;
    }

    var container = document.querySelector(CONFIG.FBT_CONTAINER_SELECTOR);
    if (container) {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      processFBTWidget(container);
    }
  }

  /**
   * Set up MutationObserver to watch for FBT widget rendering
   */
  function setupMutationObserver() {
    var targetNode = document.body;

    if (!targetNode || typeof targetNode !== 'object') {
      return;
    }

    observer = new MutationObserver(function () {
      if (initialProcessingComplete || isProcessing) {
        if (observer) {
          observer.disconnect();
          observer = null;
        }
        return;
      }

      findAndProcessFBTWidget();

      if (initialProcessingComplete && observer) {
        observer.disconnect();
        observer = null;
      }
    });

    observer.observe(targetNode, { childList: true, subtree: true });
    findAndProcessFBTWidget();
  }

  /**
   * Initialize the FBT Variant Hider
   */
  function init() {
    // Only run on product pages
    if (!window.location.pathname.match(/\/products\//)) {
      return;
    }

    if (typeof MutationObserver !== 'undefined') {
      setupMutationObserver();
    } else {
      // Fallback for browsers without MutationObserver
      setTimeout(findAndProcessFBTWidget, 1000);
      setTimeout(findAndProcessFBTWidget, 3000);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
